import { filterTreeItems } from '@aibees/elephant/components/checkbox-tree-view/utils';
import { SearchFilter } from '@aibees/elephant/feature/search-filters';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { useMemo } from 'react';
import { TreeItemData } from '../../../../../components/checkbox-tree-view';
import { useIsSearchQueryReachedMinimumLengthThreshold } from './use-is-search-query-reached-minimum-length-threshold';

interface UseFilteredTreeItemsParams {
  items: TreeViewBaseItem<TreeItemData>[] | undefined;
  debouncedSearchQuery: string | undefined;
  isGettingDataFromApi: boolean;
  metadata?: SearchFilter['metadata'];
  customOptionsLimit?: number;
}

export function useFilteredTreeItems({
  items,
  debouncedSearchQuery,
  isGettingDataFromApi,
  metadata,
  customOptionsLimit,
}: UseFilteredTreeItemsParams) {
  const isSearchQueryReachedMinimumLengthThreshold =
    useIsSearchQueryReachedMinimumLengthThreshold(
      debouncedSearchQuery,
      metadata?.autocomplete?.searchTriggerMinLength
    );

  return useMemo(() => {
    // If getting data from API or search query is too short, return limited items
    if (
      (isGettingDataFromApi || !isSearchQueryReachedMinimumLengthThreshold) &&
      !metadata?.autocomplete?.onlySearchInResults
    ) {
      return customOptionsLimit
        ? items?.slice(0, customOptionsLimit) ?? []
        : items ?? [];
    } else {
      // Otherwise, filter the items
      return (
        filterTreeItems(
          items,
          debouncedSearchQuery,
          isSearchQueryReachedMinimumLengthThreshold,
          customOptionsLimit
        ) ?? []
      );
    }
  }, [
    items,
    isGettingDataFromApi,
    isSearchQueryReachedMinimumLengthThreshold,
    metadata?.autocomplete?.onlySearchInResults,
    customOptionsLimit,
    debouncedSearchQuery,
  ]);
}
